<template>
  <div
    v-if="open && field && localDraft"
    class="modal fade show d-block gt-column-filter-modal"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    style="background-color: rgba(0, 0, 0, 0.45)"
    @click.self="onClose"
  >
    <div
      class="modal-dialog modal-dialog-centered modal-lg gt-column-filter-modal-dialog"
      role="document"
      @click.stop
    >
      <div class="modal-content">
        <div class="modal-header py-2">
          <h5 class="modal-title text-truncate">
            {{ $t(field.label) }}
          </h5>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary border-0"
            :aria-label="$t('Close')"
            @click="onClose"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <!-- Account Statement date columns: native calendar + Between (local only). -->
          <div v-if="isDateField" class="as-date-filter">
            <div
              class="input-group input-group-sm flex-nowrap mb-1 date-filter-row"
            >
              <select
                :value="localDraft.filterOperator || '='"
                class="form-select form-select-sm filter-op-select"
                @change="onOperatorChange($event.target.value)"
              >
                <option value="=">{{ $t("equal to") }}</option>
                <option value=">">{{ $t("After") }}</option>
                <option value="<">{{ $t("Before") }}</option>
                <option value=">=">{{ $t("On or after") }}</option>
                <option value="<=">{{ $t("On or before") }}</option>
                <option value="between">{{ $t("Between") }}</option>
                <option value="!=">{{ $t("Different from") }}</option>
                <option value="is_null">{{ $t("Is empty") }}</option>
                <option value="is_not_null">{{ $t("Is not empty") }}</option>
              </select>
              <input
                type="date"
                class="form-control form-control-sm flex-grow-1 min-w-0"
                :value="(localDraft.tempDateRange && localDraft.tempDateRange.start) || ''"
                :disabled="isEmptyDateOperator"
                @input="onDateRangeInput({ part: 'start', value: $event.target.value })"
              />
              <input
                v-if="localDraft.filterOperator === 'between'"
                type="date"
                class="form-control form-control-sm flex-grow-1 min-w-0"
                :value="(localDraft.tempDateRange && localDraft.tempDateRange.end) || ''"
                @input="onDateRangeInput({ part: 'end', value: $event.target.value })"
              />
            </div>
            <button
              v-if="isDraftActive"
              type="button"
              class="btn btn-link btn-sm text-danger p-0 float-end"
              @click="clearDraft"
            >
              <i class="fas fa-times"></i>
            </button>
            <button
              type="button"
              class="btn btn-link btn-sm text-muted px-0 py-0 mt-1"
              @click="clearDateRange"
            >
              <i class="fas fa-times me-1"></i>{{ $t("Clear dates") }}
            </button>
          </div>

          <FiltersFieldControls
            v-else
            :field="field"
            :model-name="modelName"
            :filter-value="localDraft.filterValue"
            :filter-operator="localDraft.filterOperator || '='"
            :temp-date-range="
              localDraft.tempDateRange || {
                start: '',
                end: '',
              }
            "
            :list-options="listOptions"
            :reference-options-list="[]"
            :users-options="[]"
            :users-options-loading="false"
            :list-options-sub-field="[]"
            :searching-reference="false"
            :is-date-field="false"
            :filter-active="isDraftActive"
            hide-label-row
            :multiselect-list-label="listLabel"
            :multiselect-list-label-sub="listLabel"
            :reference-multiselect-label="listLabel"
            @clear="clearDraft"
            @debounced-input="onDebouncedInput"
            @immediate-input="onImmediateInput"
            @operator-change="onOperatorChange"
          />
        </div>
        <div class="modal-footer py-2 d-flex gap-2 justify-content-end">
          <button
            type="button"
            class="btn btn-secondary btn-sm"
            @click="onClose"
          >
            {{ $t("Close") }}
          </button>
          <button
            type="button"
            class="btn btn-primary btn-sm"
            @click="onApply"
          >
            <i class="fas fa-filter me-1"></i>{{ $t("Filters") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FiltersFieldControls from "@/components/gotrading/FiltersFieldControls.vue";
import {
  emptyFilterSpec,
  isFilterSpecActive,
  isValuelessOperator,
} from "@/utils/columnFilterSpec";

function emptyDraft() {
  const spec = emptyFilterSpec();
  return {
    filterValue: spec.value,
    filterOperator: spec.operator,
    tempDateRange: { start: spec.start, end: spec.end },
  };
}

export default {
  name: "AccountStatementColumnFilterPopup",
  components: {
    FiltersFieldControls,
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    field: {
      type: Object,
      default: null,
    },
    /** Applied filter for this column: { value, operator, start, end } */
    initialFilter: {
      type: Object,
      default: null,
    },
    listOptions: {
      type: Array,
      default: () => [],
    },
    /** Se pasa a FiltersFieldControls; identifica el listado que filtra. */
    modelName: {
      type: String,
      default: "account_statement",
    },
  },
  data() {
    return {
      localDraft: null,
    };
  },
  computed: {
    isDateField() {
      return this.field && this.field.type === "date";
    },
    isEmptyDateOperator() {
      const op = (this.localDraft && this.localDraft.filterOperator) || "=";
      return isValuelessOperator(op);
    },
    isDraftActive() {
      return isFilterSpecActive(this.localDraftToSpec());
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(isOpen) {
        if (isOpen) this.resetLocalDraftFromInitial();
        else this.localDraft = null;
      },
    },
    "field.key"(next, prev) {
      if (this.open && next && next !== prev) {
        this.resetLocalDraftFromInitial();
      }
    },
  },
  methods: {
    listLabel(opt) {
      if (!opt) return "";
      return opt.label || opt.name || String(opt._id || "");
    },
    cloneValue(val) {
      if (val === undefined) return null;
      if (val === null) return null;
      if (Array.isArray(val)) return val.slice();
      if (typeof val === "object") return { ...val };
      return val;
    },
    resetLocalDraftFromInitial() {
      const existing = this.initialFilter || {};
      this.localDraft = {
        filterValue:
          existing.value !== undefined ? this.cloneValue(existing.value) : null,
        filterOperator: existing.operator || "=",
        tempDateRange: {
          start: existing.start || "",
          end: existing.end || "",
        },
      };
    },
    localDraftToSpec() {
      const d = this.localDraft || emptyDraft();
      if (this.isDateField) {
        const start = (d.tempDateRange && d.tempDateRange.start) || "";
        const end = (d.tempDateRange && d.tempDateRange.end) || "";
        return {
          value: null,
          operator: d.filterOperator || "=",
          start,
          end: d.filterOperator === "between" ? end : "",
        };
      }
      return {
        value: d.filterValue,
        operator: d.filterOperator || "=",
        start: (d.tempDateRange && d.tempDateRange.start) || "",
        end: (d.tempDateRange && d.tempDateRange.end) || "",
      };
    },
    onDebouncedInput(value) {
      if (!this.localDraft) return;
      this.$set(this.localDraft, "filterValue", value);
    },
    onImmediateInput(value) {
      if (!this.localDraft) return;
      const v = Array.isArray(value)
        ? value.slice()
        : this.cloneValue(value);
      this.$set(this.localDraft, "filterValue", v);
    },
    onOperatorChange(op) {
      if (!this.localDraft) return;
      this.$set(this.localDraft, "filterOperator", op || "=");
      if (isValuelessOperator(op)) {
        this.clearDateRange();
      }
      if (op !== "between" && this.localDraft.tempDateRange) {
        this.$set(this.localDraft.tempDateRange, "end", "");
      }
    },
    onDateRangeInput(payload) {
      if (!this.localDraft) return;
      const part = payload && payload.part;
      const val = payload && payload.value;
      if (!this.localDraft.tempDateRange) {
        this.$set(this.localDraft, "tempDateRange", { start: "", end: "" });
      }
      if (part) this.$set(this.localDraft.tempDateRange, part, val);
    },
    clearDateRange() {
      if (!this.localDraft) return;
      this.$set(this.localDraft, "tempDateRange", { start: "", end: "" });
    },
    clearDraft() {
      this.localDraft = emptyDraft();
    },
    onClose() {
      this.$emit("close");
    },
    onApply() {
      const spec = this.localDraftToSpec();
      this.$emit("apply", {
        active: isFilterSpecActive(spec),
        spec,
      });
    },
  },
};
</script>

<style scoped>
.gt-column-filter-modal {
  z-index: 1055;
}

.gt-column-filter-modal-dialog {
  max-height: calc(100vh - 2rem);
}

.gt-column-filter-modal-dialog .modal-content {
  max-height: calc(100vh - 2rem);
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.gt-column-filter-modal-dialog .modal-body {
  overflow: visible;
}

.gt-column-filter-modal :deep(.multiselect--active) {
  z-index: 10060 !important;
}

.gt-column-filter-modal :deep(.multiselect__content-wrapper) {
  z-index: 10061 !important;
}

.as-date-filter .filter-op-select {
  flex: 0 0 auto;
  width: auto;
  max-width: 11rem;
}
</style>
